import { t } from "@lingui/macro";
import { Info, TrendUp, Question, Eye, DownloadSimple } from "@phosphor-icons/react";
import { Button, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { AnimatePresence, motion } from "framer-motion";

import { useResumeStatistics } from "@/client/services/resume";
import { useResumeStore } from "@/client/stores/resume";

export const StatisticsSection = () => {
  const id = useResumeStore((state) => state.resume.id);
  const isPublic = useResumeStore((state) => state.resume.visibility === "public");
  const setValue = useResumeStore((state) => state.setValue);

  const { statistics } = useResumeStatistics(id, isPublic);

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6 border-b border-gray-200 pb-5">
        <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
          <TrendUp size={20} className="text-gray-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-[#16344c]">{t`Statistics`}</h3>
          <p className="text-[11px] text-gray-400">{t`Resume analytics`}</p>
        </div>
        <div className="ml-auto">
          <Tooltip content={t`Learn more about stats`}>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors">
              <Question size={18} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">
      <div className="space-y-6">

        {/* Info Alert for non-public resumes */}
        <AnimatePresence>
          {!isPublic && (
            <motion.div
              initial={{ opacity: 0, y: -20, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
              className="p-4 rounded-xl border border-blue-500/20 bg-blue-50 flex items-start gap-3"
            >
              <div className="mt-0.5">
                <Info size={18} weight="fill" className="text-blue-400" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-[#16344c]">{t`Public resume required`}</h4>
                <p className="text-[10px] text-gray-500 leading-relaxed">
                  {t`Statistics are available only for public resumes. You can track the number of views or downloads by enabling public sharing.`}
                </p>
                <button
                  className="text-[10px] text-[#268599] hover:text-[#16344c] hover:underline font-medium pt-1 inline-flex items-center gap-1 transition-colors"
                  onClick={() => setValue("visibility", "public")}
                >
                  {t`Enable sharing now`} &rarr;
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mt-4">

          {/* Views Card */}
          <div className={cn(
            "relative overflow-hidden rounded-xl border p-4 transition-all h-32 flex flex-col justify-between group",
            isPublic ? "bg-white border-gray-200 hover:border-[#268599]/30 hover:bg-[#268599]/5" : "bg-gray-50 border-gray-200 opacity-50"
          )}>
            {/* Animated background glow */}
            {isPublic && (
              <div className="absolute -right-4 -top-4 w-16 h-16 bg-[#268599]/10 blur-xl rounded-full transition-opacity opacity-50 group-hover:opacity-100"></div>
            )}

            <div className="relative z-10 w-8 h-8 rounded-lg flex items-center justify-center transition-colors">
              <div className={cn("w-full h-full rounded-lg flex items-center justify-center transition-colors", isPublic ? "bg-[#268599]/10 text-[#268599]" : "bg-gray-100 text-gray-400")}>
                <Eye size={18} weight="fill" />
              </div>
            </div>

            <div className="relative z-10 space-y-0.5">
              <div className={cn("text-[10px] uppercase font-bold tracking-wider", isPublic ? "text-gray-400" : "text-gray-300")}>
                {t`Views`}
              </div>
              <div className={cn(
                "text-3xl font-bold font-mono transition-all",
                isPublic ? "text-[#16344c]" : "blur-[4px] text-gray-300"
              )}>
                {isPublic ? (statistics?.views ?? 0) : "000"}
              </div>
            </div>
          </div>

          {/* Downloads Card */}
          <div className={cn(
            "relative overflow-hidden rounded-xl border p-4 transition-all h-32 flex flex-col justify-between group",
            isPublic ? "bg-white border-gray-200 hover:border-blue-500/30 hover:bg-blue-50" : "bg-gray-50 border-gray-200 opacity-50"
          )}>
            {/* Animated background glow */}
            {isPublic && (
              <div className="absolute -right-4 -top-4 w-16 h-16 bg-blue-500/10 blur-xl rounded-full transition-opacity opacity-50 group-hover:opacity-100"></div>
            )}

            <div className="relative z-10 w-8 h-8 rounded-lg flex items-center justify-center transition-colors">
              <div className={cn("w-full h-full rounded-lg flex items-center justify-center transition-colors", isPublic ? "bg-blue-500/10 text-blue-500" : "bg-gray-100 text-gray-400")}>
                <DownloadSimple size={18} weight="fill" />
              </div>
            </div>

            <div className="relative z-10 space-y-0.5">
              <div className={cn("text-[10px] uppercase font-bold tracking-wider", isPublic ? "text-gray-400" : "text-gray-300")}>
                {t`Downloads`}
              </div>
              <div className={cn(
                "text-3xl font-bold font-mono transition-all",
                isPublic ? "text-[#16344c]" : "blur-[4px] text-gray-300"
              )}>
                {isPublic ? (statistics?.downloads ?? 0) : "000"}
              </div>
            </div>
          </div>

        </div>
      </div>
      </div>{/* end white card */}
    </div>
  );
};
